// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import classNames from 'classnames/bind';
import { FormattedMessage, defineMessages, intlShape } from 'react-intl';
import { Mobile, Default } from '../../../../utils/responsive';
import Modal from '../../../../elements/Modal/Modal';
import analytics from '../../../../analytics';
import { FormValidator } from '../../../../utils/form';

import style from './HowToOrderModal.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import inputStyle from '../../../../assets/styles/input.scss';
import step1 from '../../../../assets/img/stores/steps/info1.png'
import step2 from '../../../../assets/img/stores/steps/info2.png'
import step3 from '../../../../assets/img/stores/steps/info3.png'

import type { Node } from 'react';
import type { OnlineStore } from '../../../../entities';
import type { FormValidationResult } from '../../../../utils/form';
import { urlVerifier } from '../../../../utils/string';

type Props = {
  +open: boolean;
  +handleClose: () => void;
  +store: OnlineStore;
  +goToRedirect: (route: string) => void;
};

type State = {
  showForm: boolean;
  url: {
    value: string;
    errors?: string[];
  };
};

type Step = {
  img: string;
  text: string;
};

const settings = {
  dots: true,
  arrows: false,
  draggable: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  infinite: false,
  dotsClass: classNames('slick-dots', style.dots),
};

const steps: Step[] = [
  {
    img: step1,
    text: 'stores.carousel.step1.text',
  },
  {
    img: step2,
    text: 'stores.carousel.step2.text',
  }, {
    img: step3,
    text: 'stores.carousel.step3.text',
  }
];

const messages = defineMessages({
  titlePartOne: {
    id: 'stores.carousel.title.partOne',
  },
  titlePartTwo: {
    id: 'stores.carousel.title.partTwo',
  },
});

class HowToOrderModal extends Component<Props, State> {
  formValidator: FormValidator;
  state = {
    showForm: false,
    url: {
      value: '',
    },
  };

  constructor(props: Props) {
    super(props);
    this.formValidator = new FormValidator({
      url: {
        required: 'required',
      },
    });
  }

  componentDidMount() {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 150);
  }

  componentWillReceiveProps() {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 150);
  }

  handleFocus(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name } = e.target;
      this.setState({ [name]: { value: this.state[name].value } });
    }
  }

  handleBlur(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      const errors = this.formValidator.validateField(name, value);
      if (errors.length && (errors[0] === 'urlFormat')) {
        this.setState({ [name]: { value, errors } });
      } else {
        this.setState({ [name]: { value } });
      }
    }
  }

  handleChange(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleSubmit(e: Event) {
    e.preventDefault();
    const { url } = this.state;
    const { goToRedirect } = this.props;
    const errors = this.formValidator.validateForm({
      url: url.value,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) =>
        this.setState((state: State) => ({
          [error.field]: {
            ...state[error.field],
            errors: error.errors,
          }
        }))
      );
    } else {
      if (urlVerifier(url.value) || url.value.startsWith('http')) {
        goToRedirect(`/new-order/product?initialQuery=${url.value}`);
      } else {
        goToRedirect(`/new-order/search?initialQuery=${url.value}`);
      }
    }
  }

  buildInfos(): Node[] {
    const { store } = this.props;
    return steps.map((step: Step, index: number) => (
      <article className={style.step} key={index}>1
        <section className={style.picture}>
          <img className={style.backImage} src={step.img} alt="Qempo"/>
          {index !== 2 && <img className={style.logoStore}
                               src={store.logo}
                               alt={store.name}/>}
          <div className={style.number}>{index + 1}</div>
        </section>
        <FormattedMessage id={step.text}>{
          text => <p>{text}</p>
        }</FormattedMessage>
      </article>)
    );
  }

  handleClose() {
    const { handleClose } = this.props;
    this.setState({ showForm: false });
    handleClose();
  }

  render() {
    const { open, store } = this.props;
    const { showForm, url } = this.state;
    const urlError = url && url.errors && FormValidator.buildErrorLabel(url.errors, {
      required: 'newOrder.scrap.error.required',
      urlFormat: 'newOrder.scrap.error.format',
    });

    return (
      <Modal open={open}
             handleClose={this.handleClose.bind(this)}
             width={720}
             height={616}>
        {showForm ? (
            <section className={style.formToOrder}>
              <FormattedMessage id="stores.carousel.form.title">{
                text => <h2>{text}</h2>
              }</FormattedMessage>
              <FormattedMessage id="stores.carousel.form.subtitle">{
                text => <p>{text}</p>
              }</FormattedMessage>
              <form onSubmit={this.handleSubmit.bind(this)}>
                <FormattedMessage id="stores.carousel.form.placeholder">{
                  text => <input type="text"
                                 name="url"
                                 className={classNames(inputStyle.input,
                                   { [inputStyle.red]: url.errors && url.errors.length })}
                                 onFocus={this.handleFocus.bind(this)}
                                 onBlur={this.handleBlur.bind(this)}
                                 onChange={this.handleChange.bind(this)}
                                 autoComplete="disabled"
                                 placeholder={text}/>
                }</FormattedMessage>
                <FormattedMessage id="stores.carousel.form.button">{
                  text => <button className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}>{text}</button>
                }</FormattedMessage>
              </form>
            </section>
          ) :
          <section className={style.howToOrder}>
            <Mobile>
              <h2>{this.context.intl.formatMessage(messages.titlePartOne)} <span>{` ${store.name}`}</span><br/>
                {` ${this.context.intl.formatMessage(messages.titlePartTwo)}`}</h2>
              <section className={style.carousel}>
                <Slider ref="slick" {...settings}>
                  {this.buildInfos()}
                </Slider>
              </section>
            </Mobile>
            <Default>
              <h2>{this.context.intl.formatMessage(messages.titlePartOne)} <span>{` ${store.name}`}</span>
                {` ${this.context.intl.formatMessage(messages.titlePartTwo)}`}</h2>
              <section className={style.carousel}>
                {this.buildInfos()}
              </section>
            </Default>
            <FormattedMessage id="stores.carousel.button">{
              text => <a className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                         onClick={() => this.setState({ showForm: true })}
                         href={store.url}
                         target="_blank">{`${text} ${store.name}`}</a>
            }</FormattedMessage>
          </section>
        }

      </Modal>
    );
  }
}

HowToOrderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  store: PropTypes.object.isRequired,
  goToRedirect: PropTypes.func.isRequired,
};

HowToOrderModal.contextTypes = {
  intl: intlShape,
};

export default HowToOrderModal;



// WEBPACK FOOTER //
// ./src/modules/stores/components/HowToOrderModal/HowToOrderModal.jsx